import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getHowToApproach, mapApiHowToApproachToUi } from '../../../services/approach.js';

/**
 * The How to approach card of one record (CT-5714). Called ONCE by the profile
 * page, like the entity header and the Next Best Action suggestions.
 *
 * `refresh` is quiet: a change made in Intelligence (an insight or trait
 * reported) re-reads the card without flashing its skeleton.
 */
export function useHowToApproach({ entityType, entityId } = {}) {
  const [approach, setApproach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchIdRef = useRef(0);
  // The record open NOW. The page stays mounted across records, so a `refresh`
  // captured for the previous one (a report there that answers late) must
  // neither show that record's card nor cancel this one's request.
  const recordKey = `${entityType}/${entityId}`;
  const openRecordRef = useRef(recordKey);

  const fetchData = useCallback(
    async ({ quiet = false } = {}) => {
      if (!entityType || !entityId) return;
      if (`${entityType}/${entityId}` !== openRecordRef.current) return;
      const requestId = ++fetchIdRef.current;
      if (!quiet) {
        setLoading(true);
        setError(null);
      }

      try {
        const { body } = await getHowToApproach({ entityType, entityId });
        if (requestId !== fetchIdRef.current) return;
        setApproach(body ? mapApiHowToApproachToUi(body) : null);
        setError(null);
      } catch (err) {
        if (requestId !== fetchIdRef.current || quiet) return;
        setApproach(null);
        setError(err?.message ?? 'Failed to load How to approach');
      } finally {
        if (requestId === fetchIdRef.current) setLoading(false);
      }
    },
    [entityType, entityId],
  );

  const refetch = useCallback(() => fetchData(), [fetchData]);
  const refresh = useCallback(() => fetchData({ quiet: true }), [fetchData]);

  // Declared BEFORE the fetch effect, so the new record is "open" when it loads.
  useEffect(() => {
    openRecordRef.current = recordKey;
  }, [recordKey]);

  useEffect(() => {
    fetchData();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchData]);

  return useMemo(() => ({ approach, loading, error, refetch, refresh }), [approach, loading, error, refetch, refresh]);
}

export default useHowToApproach;
